import { useEffect, useRef, useState } from 'react';

/**
 * Login-page decorative media, reusing a curated subset of the landing page's
 * video wall (copied into /public/login-assets). PERFORMANCE IS THE POINT here:
 * - Posters (tiny ~4-16 KB webp) render immediately and are the only thing that
 *   blocks first paint of the panel.
 * - The mp4s are NEVER render-blocking: they carry preload="none" and their src
 *   is only attached after the page has gone idle (requestIdleCallback / load),
 *   staggered a few tiles at a time so they don't compete with Firebase / GIS.
 * - Mobile never loads any mp4 (the wall isn't rendered there; the backdrop is
 *   posters only).
 * - prefers-reduced-motion → posters only, no autoplay, no drift.
 */

const CLIP_BASE = '/login-assets/clips';
const POSTER_BASE = '/login-assets/posters';

type Tile = { name: string; a: 'p' | 'l' };

// Left and right columns use disjoint clip sets so the two sides never mirror
// each other. Each column duplicates its own tiles for the seamless -50% loop.
const LEFT_COLUMN_TILES: Tile[] = [
  { name: 'dt-ugc-1', a: 'l' },
  { name: 'mt-avatar-2', a: 'p' },
  { name: 'dt-animation-1', a: 'l' },
  { name: 'mt-food-21', a: 'p' },
  { name: 'dt-travel-1', a: 'l' },
];

const RIGHT_COLUMN_TILES: Tile[] = [
  { name: 'mt-product-21', a: 'p' },
  { name: 'dt-food-1', a: 'l' },
  { name: 'dt-avatar-1', a: 'l' },
  { name: 'dt-product-2', a: 'l' },
  { name: 'dt-avatar-2', a: 'l' },
];

// A few posters for the mobile blurred backdrop (posters only, no mp4s).
const BACKDROP_POSTERS = ['dt-animation-1', 'mt-avatar-2', 'dt-travel-1', 'mt-product-21'];

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function WallTile({
  tile,
  index,
  videosEnabled,
}: {
  tile: Tile;
  index: number;
  videosEnabled: boolean;
}) {
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    if (!videosEnabled) return;
    // Stagger tiles so we never fire 10 video requests at once.
    const timer = window.setTimeout(() => setShowVideo(true), index * 300);
    return () => window.clearTimeout(timer);
  }, [videosEnabled, index]);

  return (
    <div
      className={`relative mb-4 w-full overflow-hidden rounded-2xl bg-surface-light ${
        tile.a === 'p' ? 'aspect-[9/16]' : 'aspect-video'
      }`}
    >
      <img
        src={`${POSTER_BASE}/${tile.name}.webp`}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {showVideo && (
        <video
          src={`${CLIP_BASE}/${tile.name}.mp4`}
          poster={`${POSTER_BASE}/${tile.name}.webp`}
          muted
          loop
          playsInline
          autoPlay
          preload="none"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-1000 ease-out"
          onCanPlay={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        />
      )}
    </div>
  );
}

function ScrollingColumn({
  tiles,
  videosEnabled,
  staggerOffset,
  animationDelay,
  animationClass,
  reducedMotion,
}: {
  tiles: Tile[];
  videosEnabled: boolean;
  staggerOffset: number;
  animationDelay?: string;
  animationClass: string;
  reducedMotion: boolean;
}) {
  const doubled = [...tiles, ...tiles];

  return (
    <div className="flex-1 overflow-hidden">
      <div
        className={`p-2 will-change-transform ${reducedMotion ? '' : animationClass}`}
        style={animationDelay ? { animationDelay } : undefined}
      >
        {doubled.map((tile, i) => (
          <WallTile
            key={`${tile.name}-${i}`}
            tile={tile}
            index={staggerOffset + (i % tiles.length)}
            videosEnabled={videosEnabled}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Desktop-only auto-scrolling video wall for the login page's right panel.
 * Renders nothing meaningful until posters load; videos hydrate after idle.
 */
export function LoginVideoWall() {
  const [videosEnabled, setVideosEnabled] = useState(false);
  const reducedMotion = useRef(prefersReducedMotion());

  useEffect(() => {
    if (reducedMotion.current) return; // posters only

    let idleId: number | undefined;
    let cancelled = false;

    const enable = () => {
      if (cancelled) return;
      const ric = (
        window as unknown as { requestIdleCallback?: (cb: () => void) => number }
      ).requestIdleCallback;
      if (ric) {
        idleId = ric(() => !cancelled && setVideosEnabled(true));
      } else {
        idleId = window.setTimeout(() => !cancelled && setVideosEnabled(true), 1200);
      }
    };

    if (document.readyState === 'complete') {
      enable();
    } else {
      window.addEventListener('load', enable, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', enable);
      const cancelIdle = (
        window as unknown as { cancelIdleCallback?: (id: number) => void }
      ).cancelIdleCallback;
      if (idleId !== undefined) {
        if (cancelIdle) cancelIdle(idleId);
        else window.clearTimeout(idleId);
      }
    };
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="flex h-full gap-4 px-4">
        <ScrollingColumn
          tiles={LEFT_COLUMN_TILES}
          videosEnabled={videosEnabled}
          staggerOffset={0}
          animationClass="animate-login-wall"
          reducedMotion={reducedMotion.current}
        />
        <ScrollingColumn
          tiles={RIGHT_COLUMN_TILES}
          videosEnabled={videosEnabled}
          staggerOffset={LEFT_COLUMN_TILES.length}
          animationClass="animate-login-wall-reverse"
          animationDelay="-60s"
          reducedMotion={reducedMotion.current}
        />
      </div>
    </div>
  );
}

/**
 * Mobile background: a blurred poster collage (posters only, ~30 KB) so small
 * screens still feel alive without ever downloading an mp4.
 */
export function LoginPosterBackdrop() {
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
      <div className="grid h-full w-full grid-cols-2 gap-2 scale-110 blur-2xl opacity-40">
        {BACKDROP_POSTERS.map((name) => (
          <img
            key={name}
            src={`${POSTER_BASE}/${name}.webp`}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ))}
      </div>
      <div className="absolute inset-0 bg-background/80" />
    </div>
  );
}
